/**
 * Public CSAE (Child Sexual Abuse & Exploitation) Standards page.
 *
 * Mounted at /sacsaae via the public layout. No auth required — this page
 * needs to be world-readable for:
 *  - Google Play Store's CSAE compliance review
 *  - Apple App Store's Standards 1.1.4 review
 *  - EU Digital Services Act (DSA) transparency requirements
 *  - NCMEC / WeProtect Global Alliance trusted-reporter status
 *
 * Content is written in plain English and references the external
 * standards we align with. If you revise any of the numbered sections,
 * bump the "lastUpdated" date below.
 */
import { Helmet } from "react-helmet-async";
import { ShieldAlert, Mail, ExternalLink } from "lucide-react";

const Section = ({ title, children, testId }) => (
  <section className="mb-10" data-testid={testId}>
    <h2
      className="text-base sm:text-lg font-semibold mb-3"
      style={{ color: "var(--color-text-primary)" }}
    >
      {title}
    </h2>
    <div
      className="text-sm leading-relaxed space-y-3"
      style={{ color: "var(--color-text-secondary)" }}
    >
      {children}
    </div>
  </section>
);

const OutLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80"
    style={{ color: "var(--color-brand-primary, #a78bfa)" }}
  >
    {children}
    <ExternalLink className="w-3 h-3" />
  </a>
);

const SACSAAEPage = () => {
  // Bumped only when the policy materially changes. Shown at top so
  // reviewers and users can spot updates at a glance.
  const lastUpdated = "April 28, 2026";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="sacsaae-page">
      <Helmet>
        <title>Standards Against Child Sexual Abuse & Exploitation · Evoluras</title>
        <meta
          name="description"
          content="Evoluras' zero-tolerance policy and detection, reporting, and enforcement standards against child sexual abuse and exploitation (CSAE)."
        />
      </Helmet>

      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: "rgba(239, 68, 68, 0.18)" }}
        >
          <ShieldAlert className="w-5 h-5" style={{ color: "#ef4444" }} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ color: "var(--color-text-primary)" }}>
            Standards Against Child Sexual Abuse & Exploitation
          </h1>
          <p className="text-xs" style={{ color: "var(--color-text-placeholder)" }}>
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      {/* Emergency banner */}
      <div
        className="rounded-xl p-4 mb-10 mt-6"
        style={{
          background: "rgba(239, 68, 68, 0.08)",
          border: "1px solid rgba(239, 68, 68, 0.35)",
        }}
        data-testid="csae-emergency-banner"
      >
        <p className="text-sm font-semibold mb-1" style={{ color: "#fca5a5" }}>
          Emergency or life-threatening situation?
        </p>
        <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
          If a child is in immediate danger, contact local law enforcement first.
          In the United States dial <strong>911</strong>. You can also report suspected child
          sexual abuse or exploitation directly to the National Center for Missing &amp;
          Exploited Children (NCMEC) CyberTipline at{" "}
          <OutLink href="https://report.cybertip.org">report.cybertip.org</OutLink> or{" "}
          <strong>1-800-843-5678</strong>.
        </p>
      </div>

      {/* Our commitment */}
      <Section title="1. Our Zero-Tolerance Commitment" testId="csae-section-commitment">
        <p>
          Evoluras maintains a zero-tolerance policy against child sexual abuse and
          exploitation (CSAE) material, grooming, solicitation of a minor, and any
          content, conduct, or mechanism that sexualizes or endangers children under 18.
          These behaviors are prohibited everywhere on the platform — in public
          profiles, gratitude walls, insight exchanges, network invitations, direct
          messages, uploaded media, and any third-party integration operated on
          Evoluras' behalf.
        </p>
        <p>
          This policy applies globally and regardless of consent. Claims that the
          depicted or described minor "consented," is "mature for their age," or
          "looks older" are not a defense and will not reduce the severity of our
          enforcement response.
        </p>
      </Section>

      {/* External standards alignment */}
      <Section title="2. External Standards We Align With" testId="csae-section-standards">
        <p>Evoluras' CSAE program is built on and audited against:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>WeProtect Global Alliance Model National Response.</strong> See{" "}
            <OutLink href="https://www.weprotect.org/the-model-national-response/">
              weprotect.org
            </OutLink>
            .
          </li>
          <li>
            <strong>Tech Coalition Voluntary Principles to Counter Online Child Sexual
            Exploitation and Abuse.</strong>{" "}
            <OutLink href="https://www.technologycoalition.org/">
              technologycoalition.org
            </OutLink>
            .
          </li>
          <li>
            <strong>United States federal law</strong> — 18 U.S.C. §§ 2251–2260A
            (production, distribution and possession of child pornography), the PROTECT
            Act of 2003, and mandatory reporting to NCMEC under 18 U.S.C. § 2258A.
          </li>
          <li>
            <strong>EU Digital Services Act (DSA)</strong> — Articles 16, 18, and 22
            on content notice, priority reporter channels, and law-enforcement
            cooperation.
          </li>
          <li>
            <strong>United Kingdom Online Safety Act 2023</strong> — illegal-content
            duties for CSAM, grooming, and solicitation under Schedule 6.
          </li>
          <li>
            <strong>UN Convention on the Rights of the Child (CRC)</strong>, Optional
            Protocol on the sale of children, child prostitution, and child
            pornography.
          </li>
          <li>
            <strong>INHOPE network of reporting hotlines</strong> for cross-border
            takedowns.
          </li>
          <li>
            <strong>NCMEC CyberTipline</strong> for U.S. mandatory reporting.
          </li>
        </ul>
      </Section>

      {/* Prohibited */}
      <Section title="3. Prohibited Content and Conduct" testId="csae-section-prohibited">
        <p>The following is strictly forbidden on Evoluras:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Any visual depiction, including photorealistic generated, drawn, or
            AI-generated imagery, of a person under 18 engaged in sexually explicit
            conduct or posed in a sexualized manner.</li>
          <li>Text, audio, or video that describes, solicits, or incites the sexual
            abuse or exploitation of a minor, including "chat roleplay" or fictional
            framing that normalizes such conduct.</li>
          <li>Grooming — any attempt by an adult to build a relationship, trust, or
            emotional connection with a minor for the purpose of later sexual abuse,
            exploitation, or trafficking.</li>
          <li>Sextortion, non-consensual intimate-image sharing, or the threat of
            such, involving a minor.</li>
          <li>Sharing personal contact information, location, travel plans, or
            meeting arrangements with a minor in a sexual or exploitative context.</li>
          <li>Commercial transactions, advertising, or "shopping links" for CSAM,
            child-sex tourism, or child trafficking.</li>
          <li>Referral to external platforms, dark-web services, or peer-to-peer
            networks where CSAM or exploitation content is traded.</li>
          <li>Creating, distributing, or soliciting AI-generated or deepfaked
            sexualized imagery of a real or fictional minor, regardless of the
            purported age of the subject.</li>
        </ul>
      </Section>

      {/* Detection */}
      <Section title="4. Detection and Proactive Measures" testId="csae-section-detection">
        <p>We deploy layered detection across the upload and messaging paths:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Perceptual-hash matching</strong> of uploaded images and video
            frames against known-CSAM hash lists provided by NCMEC, the Internet
            Watch Foundation (IWF), and Project Arachnid. Matches are blocked at
            upload and escalated automatically.</li>
          <li><strong>AI-assisted classification</strong> of novel content using
            industry-grade models (PhotoDNA, Thorn Safer, or equivalent) to flag
            unknown material for human review.</li>
          <li><strong>Text + link scanning</strong> of public walls, insight notes,
            gratitude messages, and invite payloads for CSAE-related keywords,
            grooming patterns, and known offender-network URLs.</li>
          <li><strong>Account-behavior signals</strong> — rapid creation of under-age
            public profiles, bulk circle-invite activity targeting minors, age-
            inconsistent claims, and VPN / throwaway-email clusters trigger review.</li>
          <li><strong>Integrity on all media pipelines</strong> — profile pictures,
            cover images, gratitude uploads, and mobile uploads share the same
            detection stack; no path bypasses scanning.</li>
        </ul>
      </Section>

      {/* Reporting */}
      <Section title="5. User Reporting" testId="csae-section-reporting">
        <p>
          Any user or non-user can report suspected CSAE content or conduct. We
          provide three channels:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>In-app report button</strong> — visible on every profile, post,
            gratitude card, insight message, and invite. Reports are triaged within
            24 hours and escalate to NCMEC within 48 hours where a mandatory report is
            required.</li>
          <li>
            <strong>Dedicated email</strong>:{" "}
            <a
              href="mailto:csae@evoluras.com"
              className="underline underline-offset-2"
              style={{ color: "var(--color-brand-primary, #a78bfa)" }}
            >
              csae@evoluras.com
            </a>{" "}
            — monitored 24/7. Reports can be anonymous; we never disclose reporter
            identity to reported parties.
          </li>
          <li>
            <strong>Trusted-flagger channel</strong> for NGOs (INHOPE hotlines, Thorn,
            NCMEC, IWF, the Internet Society, national child-protection
            agencies): a priority queue handled within 4 hours.
          </li>
        </ul>
        <p>
          When reporting, please include any screenshot, username, URL, or date/time
          that will help us investigate. False or malicious reports are themselves a
          policy violation.
        </p>
      </Section>

      {/* Response */}
      <Section title="6. Response and Enforcement" testId="csae-section-response">
        <p>Confirmed CSAE content or conduct results in:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Immediate content removal and preservation of evidence required by
            18 U.S.C. § 2258A for at least 90 days (extended on law-enforcement
            request).</li>
          <li>Permanent account termination of the offending user, cross-checked
            against our known-offender list to prevent re-registration.</li>
          <li>Mandatory report to the <strong>NCMEC CyberTipline</strong> including
            all IP addresses, timestamps, usernames, device identifiers, and content
            hashes we have captured.</li>
          <li>Cross-reporting to law-enforcement agencies in the reported party's
            jurisdiction and, where applicable, Interpol's Child Sexual Exploitation
            Database (ICSE).</li>
          <li>Where grooming, sextortion, or imminent harm is detected, we may
            contact local emergency services directly.</li>
          <li>Review of the offender's full history on Evoluras — associated
            accounts, invited circle members, direct-message partners — for
            additional potential victims who may need support resources.</li>
        </ul>
      </Section>

      {/* Age */}
      <Section title="7. Minimum Age and Age Verification" testId="csae-section-age">
        <p>
          Evoluras is not directed to children under 13 (under 16 in the European
          Union, under 14 in Korea, under 18 in certain jurisdictions) and we do
          not knowingly collect personal information from children under those ages.
          If we learn that we have collected such information, we will delete it
          promptly. Parents or guardians who believe we have collected information
          from a child can email{" "}
          <a
            href="mailto:privacy@evoluras.com"
            className="underline underline-offset-2"
            style={{ color: "var(--color-brand-primary, #a78bfa)" }}
          >
            privacy@evoluras.com
          </a>
          .
        </p>
        <p>
          We use a combination of self-declaration at signup, behavioral signals,
          and — where risk warrants — document-based age verification through an
          independent third-party vendor to enforce age-gating.
        </p>
      </Section>

      {/* Victim support */}
      <Section title="8. Support for Victims and Survivors" testId="csae-section-support">
        <p>
          If you are a minor who has experienced abuse, or an adult survivor of
          childhood sexual abuse, the following organizations offer free, confidential
          support:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Childhelp National Child Abuse Hotline</strong> (USA):{" "}
            <OutLink href="https://www.childhelp.org">childhelp.org</OutLink> —{" "}
            <strong>1-800-422-4453</strong>
          </li>
          <li>
            <strong>RAINN — Rape, Abuse &amp; Incest National Network</strong> (USA):{" "}
            <OutLink href="https://www.rainn.org">rainn.org</OutLink> —{" "}
            <strong>1-800-656-4673</strong>
          </li>
          <li>
            <strong>NSPCC</strong> (UK):{" "}
            <OutLink href="https://www.nspcc.org.uk">nspcc.org.uk</OutLink> —{" "}
            <strong>0808 800 5000</strong>
          </li>
          <li>
            <strong>Stop It Now!</strong> (USA &amp; Ireland):{" "}
            <OutLink href="https://www.stopitnow.org">stopitnow.org</OutLink>
          </li>
          <li>
            <strong>Take It Down</strong> — free NCMEC service to help minors and
            adults remove nude or sexually explicit images of themselves taken while
            under 18:{" "}
            <OutLink href="https://takeitdown.ncmec.org">takeitdown.ncmec.org</OutLink>
          </li>
        </ul>
      </Section>

      {/* Contact */}
      <Section title="9. Contact the CSAE Team" testId="csae-section-contact">
        <p>
          Our CSAE response team is available 24 hours a day, seven days a week.
          For reports, trusted-flagger applications, or compliance inquiries:
        </p>
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4" style={{ color: "var(--color-brand-primary, #a78bfa)" }} />
          <a
            href="mailto:csae@evoluras.com"
            className="underline underline-offset-2 font-medium"
            style={{ color: "var(--color-brand-primary, #a78bfa)" }}
          >
            csae@evoluras.com
          </a>
        </div>
        <p className="text-xs" style={{ color: "var(--color-text-placeholder)" }}>
          Evoluras responds to law-enforcement requests through the same mailbox in
          accordance with applicable data-preservation and disclosure statutes.
        </p>
      </Section>

      {/* Footer */}
      <div
        className="mt-12 pt-6"
        style={{ borderTop: "1px solid rgba(255,255,255,0.10)" }}
      >
        <p className="text-xs" style={{ color: "var(--color-text-placeholder)" }}>
          This page is maintained to satisfy the Google Play "CSAE Declaration,"
          Apple App Store Guideline 1.1.4 Child Safety requirements, and the
          equivalent regulator-facing disclosures required by the EU DSA, the
          UK Online Safety Act, and any other jurisdiction where Evoluras is
          available. For the full Terms of Service and Privacy Policy, see{" "}
          <a href="/privacy" className="underline underline-offset-2" style={{ color: "var(--color-brand-primary, #a78bfa)" }}>
            /privacy
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default SACSAAEPage;
